/**
 * client/ui/bmtext.ts — BO2 bitmap-font text for DOM menus.
 *
 * OWNER: HUD-UI-AUDIO agent.
 *
 * BO2 has no TTF; menus are drawn with the same bitmap atlases as the HUD [hud.md "Fonts"].
 * A label is a <span class="bm"> sized to the bitmap text, whose glyphs are a CSS mask over
 * `background-color: currentColor` — so CSS colours, hover states and the BO2 orange pulse all
 * work. The real text stays in the DOM (transparent) for accessibility, selection and tests.
 */
import { type LuiFont, LUI_FONTS } from '../hud/bmfont';
import { WHITE, uiAssets } from '../hud/gfx';

const live = new Set<HTMLSpanElement>();
let fontsReady = false;
let fontsPromise: Promise<void> | null = null;

export function ensureFonts(): Promise<void> {
  fontsPromise ??= uiAssets().loadAllFonts().then(() => {
    fontsReady = true;
    for (const el of [...live]) {
      if (!el.isConnected) { live.delete(el); continue; }
      renderInto(el);
    }
  });
  return fontsPromise;
}

export interface BmOpts {
  size?: number;
  upper?: boolean;
  className?: string;
}

/** A bitmap-font label. */
export function bm(text: string, font: LuiFont = 'Condensed', opts: BmOpts = {}): HTMLSpanElement {
  const el = document.createElement('span');
  el.className = `bm${opts.className ? ` ${opts.className}` : ''}`;
  el.dataset['font'] = font;
  el.dataset['size'] = String(opts.size ?? LUI_FONTS[font].size);
  el.dataset['text'] = opts.upper ? text.toUpperCase() : text;
  const t = document.createElement('span');
  t.className = 'bm-t';
  t.textContent = text;
  el.appendChild(t);
  live.add(el);
  if (fontsReady) renderInto(el);
  else { void ensureFonts(); fallbackSize(el); }
  return el;
}

/** Replace a label's text in place. */
export function setBm(el: HTMLSpanElement, text: string, upper = false): void {
  const v = upper ? text.toUpperCase() : text;
  if (el.dataset['text'] === v) return;
  el.dataset['text'] = v;
  const t = el.querySelector('.bm-t');
  if (t) t.textContent = text;
  if (fontsReady) renderInto(el); else fallbackSize(el);
}

function fallbackSize(el: HTMLSpanElement): void {
  const size = Number(el.dataset['size']);
  el.style.width = `${Math.ceil((el.dataset['text'] ?? '').length * size * 0.42)}px`;
  el.style.height = `${Math.ceil(size * PAD)}px`;
}

const PAD = 1.25; // room for descenders below the box

function renderInto(el: HTMLSpanElement): void {
  const a = uiAssets();
  const font = el.dataset['font'] as LuiFont;
  const size = Number(el.dataset['size']);
  const text = el.dataset['text'] ?? '';
  const w = Math.max(1, Math.ceil(a.measure(font, text, size)));
  const h = Math.ceil(size * PAD);
  const k = 2; // supersample the mask so it survives the stage upscale
  const cv = document.createElement('canvas');
  cv.width = w * k; cv.height = h * k;
  const ctx = cv.getContext('2d');
  if (!ctx) return;
  ctx.scale(k, k);
  a.text(ctx, font, text, 0, 0, { size, color: WHITE });
  const url = `url(${cv.toDataURL()})`;
  el.style.width = `${w}px`;
  el.style.height = `${h}px`;
  el.style.maskImage = url;
  el.style.webkitMaskImage = url;
}

/** Word-wrapped bitmap text (one bm line per row), e.g. the loading-screen tip. */
export function bmWrapped(text: string, font: LuiFont, maxWidth: number, opts: BmOpts = {}): HTMLDivElement {
  const box = document.createElement('div');
  box.className = 'bm-wrap';
  const render = () => {
    const a = uiAssets();
    const size = opts.size ?? LUI_FONTS[font].size;
    const words = text.split(/\s+/);
    const lines: string[] = [];
    let cur = '';
    for (const w of words) {
      const next = cur ? `${cur} ${w}` : w;
      if (cur && a.measure(font, next, size) > maxWidth) { lines.push(cur); cur = w; } else cur = next;
    }
    if (cur) lines.push(cur);
    box.replaceChildren(...lines.map((l) => { const d = document.createElement('div'); d.appendChild(bm(l, font, opts)); return d; }));
  };
  render();
  if (!fontsReady) void ensureFonts().then(render);
  return box;
}
